import { useEffect, useState, useCallback, useRef } from 'react'
import { Mail, UserPlus, Trash2, Crown, Edit3, X, ShieldCheck, Plus, Building2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useProjectStore } from '@/store/projectStore'
import { useAuth } from '@/auth/AuthContext'
import Button from '@/components/common/Button'
import Modal from '@/components/common/Modal'
import ProjectWalletCard from './billing/ProjectWalletCard'
import InviteCostNotice from './billing/InviteCostNotice'
import SelfRegistrationCard from './billing/SelfRegistrationCard'
import ResourceBillingSection from './billing/ResourceBillingCards'
import vocabulary from './simple/vocabulary.json'
import './simple/simple.css'
import {
  type CandidateUser,
  type ProjectInvitationInfo,
  type ProjectMemberInfo,
  type ProjectRoleCatalogEntry,
  type ProjectTenantInfo,
  addMember,
  cancelProjectInvitation,
  getMyAccess,
  getRoleCatalog,
  grantToTenant,
  inviteToProject,
  listCandidateUsers,
  listFunctionalRoles,
  listFunctionalRoleUsers,
  listMembers,
  listProjectInvitations,
  listProjectTenants,
  removeMember,
  setUserFunctionalRoles,
  updateMemberRoles,
} from '@/api/team'

// The reserved super-approver functional role: its holders can approve any
// pending approval and see everyone's pending queue.
const FUNC_ADMIN_ROLE = 'func-admin'

interface OwnerInfo {
  user_id: string
  user_name: string
  email: string
}

/**
 * Renders the team-management panel content. Designed to be embedded inside
 * a Modal — no outer page padding, no page title (the modal provides those).
 * `onAfterMutate` is invoked after add/remove/invite/role-change so the
 * parent can refresh related state if needed.
 */
export default function TeamPanel({
  onAfterMutate,
  variant = 'team',
  openInvite = false,
  renderSignIn,
}: {
  onAfterMutate?: () => void
  /** `team` = the classic panel (modal); `people` = the simple Workbench's
   *  People tab: invite inline, people with access, other organizations,
   *  sign-in — the SAME state, rights checks and dialogs, regrouped. */
  variant?: 'team' | 'people'
  /** People tab opened with ?invite=1: focus the invite box. */
  openInvite?: boolean
  /** People tab: the "How people sign in" section, rendered in place. */
  renderSignIn?: () => React.ReactNode
} = {}) {
  const { currentProject } = useProjectStore()
  const { user } = useAuth()
  const projectId = currentProject?.application.folder_name || ''

  const [loading, setLoading] = useState(true)
  const [roles, setRoles] = useState<ProjectRoleCatalogEntry[]>([])
  const [members, setMembers] = useState<ProjectMemberInfo[]>([])
  const [owner, setOwner] = useState<OwnerInfo | null>(null)
  const [invitations, setInvitations] = useState<ProjectInvitationInfo[]>([])
  const [canManage, setCanManage] = useState(false)
  // Bumped after any mutation that can spend from the project wallet, so the
  // balance the manager sees is the one they actually have.
  const [walletKey, setWalletKey] = useState(0)
  // Resource billing on => the metered cards replace the legacy seat-price card.
  const [resourceBilling, setResourceBilling] = useState(false)
  const [isOwner, setIsOwner] = useState(false)

  // Functional (approver) roles — separate from the access roles above. Map is
  // keyed by user_id → that user's functional roles; `functionalRoleCatalog`
  // is the set of roles offered in the editor. Both are best-effort.
  const [functionalByUser, setFunctionalByUser] = useState<Record<string, string[]>>({})
  const [functionalRoleCatalog, setFunctionalRoleCatalog] = useState<string[]>([])

  // Customer organizations (other tenants granted the running application). Only
  // populated for owners / users with full team-management ('all') scope.
  const [projectTenants, setProjectTenants] = useState<ProjectTenantInfo[]>([])

  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [showGrantDialog, setShowGrantDialog] = useState(false)
  const [editingMember, setEditingMember] = useState<ProjectMemberInfo | null>(null)
  // The user (owner or member) whose functional roles are being edited.
  const [editingFunctional, setEditingFunctional] = useState<
    { userId: string; userName: string; current: string[] } | null
  >(null)

  const refresh = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const [catalogRes, accessRes, membersRes] = await Promise.all([
        getRoleCatalog(),
        getMyAccess(projectId),
        listMembers(projectId),
      ])
      setRoles(catalogRes.roles || [])
      setIsOwner(!!accessRes.is_owner)
      const manage =
        accessRes.is_owner || (accessRes.permissions || []).includes('project.manage_team')
      setCanManage(manage)
      setMembers(membersRes.members || [])
      setOwner(membersRes.owner || null)
      if (manage) {
        try {
          const inv = await listProjectInvitations(projectId)
          setInvitations(inv.invitations || [])
        } catch {
          setInvitations([])
        }
        // Customer organizations using this application — best-effort.
        try {
          const t = await listProjectTenants(projectId)
          setProjectTenants(t.tenants || [])
        } catch {
          setProjectTenants([])
        }
      } else {
        setInvitations([])
        setProjectTenants([])
      }

      // Functional (approver) roles — best-effort; failure leaves chips empty
      // and the editor falls back to typing roles only.
      try {
        const [frUsers, frCatalog] = await Promise.all([
          listFunctionalRoleUsers(projectId),
          listFunctionalRoles(projectId),
        ])
        const map: Record<string, string[]> = {}
        for (const u of frUsers.users || []) {
          map[u.user_id] = Array.isArray(u.functional_roles) ? u.functional_roles : []
        }
        setFunctionalByUser(map)
        setFunctionalRoleCatalog(Array.isArray(frCatalog.roles) ? frCatalog.roles : [])
      } catch {
        setFunctionalByUser({})
        setFunctionalRoleCatalog([])
      }
    } catch (err) {
      toast.error('Failed to load team')
      // eslint-disable-next-line no-console
      console.error('TeamTab refresh failed', err)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Refresh + notify the parent (so OverviewTab counts can update, etc.).
  const refreshAndNotify = useCallback(async () => {
    await refresh()
    // Adding or removing someone can move the project wallet, so re-read it too.
    setWalletKey((k) => k + 1)
    onAfterMutate?.()
  }, [refresh, onAfterMutate])

  if (!currentProject) return null
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
        <div className="spinner" />
      </div>
    )
  }

  const ownerRow = (
    <div style={ownerRowStyle}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Crown size={16} style={{ color: 'var(--color-warning)' }} />
          <strong>{owner?.user_name || '—'}</strong>
          <span style={{ color: 'var(--text-muted)' }}>{owner?.email}</span>
        </div>
        <FunctionalRoleChips roles={owner ? functionalByUser[owner.user_id] : undefined} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Full access incl. delete</span>
        {canManage && owner && (
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => setEditingFunctional({
              userId: owner.user_id,
              userName: owner.user_name,
              current: functionalByUser[owner.user_id] || [],
            })}
            title="Assign functional (approver) roles"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <ShieldCheck size={14} /> Functional roles
          </button>
        )}
      </div>
    </div>
  )

  const memberList = members.length === 0 ? (
    <p style={{ color: 'var(--text-muted)' }}>No team members yet.</p>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {members.map((m) => (
        <MemberRow
          key={m.id}
          member={m}
          roles={roles}
          canManage={canManage}
          isSelf={m.user_id === user?.user_id}
          functionalRoles={functionalByUser[m.user_id]}
          onEditFunctional={() => setEditingFunctional({
            userId: m.user_id,
            userName: m.user_name,
            current: functionalByUser[m.user_id] || [],
          })}
          onEdit={() => setEditingMember(m)}
          onRemove={async () => {
            if (!window.confirm(`Remove ${m.user_name} from this project?`)) return
            try {
              await removeMember(projectId, m.id)
              toast.success('Member removed')
              refreshAndNotify()
            } catch {
              toast.error('Failed to remove member')
            }
          }}
        />
      ))}
    </div>
  )

  const tenantList = projectTenants.length === 0 ? (
    <p style={{ color: 'var(--text-muted)' }}>
      {variant === 'people'
        ? 'No other organizations yet. Use “Share with another organization” to add one.'
        : 'No other organizations yet. Use “Grant to another organization” to onboard one.'}
    </p>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {projectTenants.map((t) => (
        <CustomerTenantCard key={t.tenant_id || t.tenant_name} tenant={t} />
      ))}
    </div>
  )

  const invitationList = invitations.length === 0 ? (
    <p style={{ color: 'var(--text-muted)' }}>No pending invitations.</p>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {invitations.map((inv) => (
        <div key={inv.id} style={memberRowStyle}>
          <div>
            <strong>{inv.email}</strong>
            <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
              {inv.role_display.map((r) => (
                <RoleChip key={r} label={r} />
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13, color: inv.is_expired ? 'var(--danger)' : 'var(--text-muted)' }}>
              {inv.is_expired ? 'Expired' : 'Pending'}
            </span>
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={async () => {
                if (!window.confirm(`Cancel invitation to ${inv.email}?`)) return
                try {
                  await cancelProjectInvitation(projectId, inv.id)
                  toast.success('Invitation cancelled')
                  refreshAndNotify()
                } catch {
                  toast.error('Failed to cancel invitation')
                }
              }}
              title="Cancel invitation"
              aria-label={`Cancel invitation to ${inv.email}`}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  )

  const roleReference = (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
      {roles.map((r) => (
        <div key={r.role} style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 6 }}>
          <strong>{r.display}</strong>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-muted)' }}>{r.description}</p>
        </div>
      ))}
    </div>
  )

  const dialogs = (
    <>
      {showAddDialog && (
        <AddExistingUserDialog
          projectId={projectId}
          roleCatalog={roles}
          onClose={() => setShowAddDialog(false)}
          onAdded={() => {
            setShowAddDialog(false)
            refreshAndNotify()
          }}
        />
      )}
      {showInviteDialog && (
        <InviteByEmailDialog
          projectId={projectId}
          roleCatalog={roles}
          onClose={() => setShowInviteDialog(false)}
          onInvited={() => {
            setShowInviteDialog(false)
            refreshAndNotify()
          }}
        />
      )}
      {showGrantDialog && (
        <GrantToOrganizationDialog
          projectId={projectId}
          onClose={() => setShowGrantDialog(false)}
          onGranted={() => {
            setShowGrantDialog(false)
            refreshAndNotify()
          }}
        />
      )}
      {editingMember && (
        <EditRolesDialog
          projectId={projectId}
          member={editingMember}
          roleCatalog={roles}
          onClose={() => setEditingMember(null)}
          onSaved={() => {
            setEditingMember(null)
            refreshAndNotify()
          }}
        />
      )}
      {editingFunctional && (
        <EditFunctionalRolesDialog
          projectId={projectId}
          userId={editingFunctional.userId}
          userName={editingFunctional.userName}
          current={editingFunctional.current}
          roleCatalog={functionalRoleCatalog}
          onClose={() => setEditingFunctional(null)}
          onSaved={() => {
            setEditingFunctional(null)
            refreshAndNotify()
          }}
        />
      )}
    </>
  )

  if (variant === 'people') {
    return (
      <PeopleLayout
        projectId={projectId}
        roles={roles}
        canManage={canManage}
        isOwner={isOwner}
        resourceBilling={resourceBilling}
        walletKey={walletKey}
        onResourceBilling={setResourceBilling}
        openInvite={openInvite}
        onInvited={refreshAndNotify}
        onAddExisting={() => setShowAddDialog(true)}
        onInviteMany={() => setShowInviteDialog(true)}
        onShareOrg={() => setShowGrantDialog(true)}
        ownerRow={ownerRow}
        memberList={memberList}
        memberCount={members.length}
        invitationList={invitationList}
        invitationCount={invitations.length}
        tenantList={tenantList}
        tenantCount={projectTenants.length}
        roleReference={roleReference}
        signIn={renderSignIn?.()}
        dialogs={dialogs}
      />
    )
  }

  return (
    <div>
      <ProjectWalletCard projectId={projectId} canManage={canManage} refreshKey={walletKey} />

      <ResourceBillingSection projectId={projectId} canManage={canManage} onEnabledChange={setResourceBilling} />

      {canManage && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button variant="secondary" size="sm" icon={<UserPlus size={16} />} onClick={() => setShowAddDialog(true)}>
            Add existing user
          </Button>
          <Button variant="primary" size="sm" icon={<Mail size={16} />} onClick={() => setShowInviteDialog(true)}>
            Invite by email
          </Button>
          <Button variant="secondary" size="sm" icon={<Building2 size={16} />} onClick={() => setShowGrantDialog(true)}>
            Grant to another organization
          </Button>
        </div>
      )}

      {/* Owner row */}
      <Section title="Project owner">{ownerRow}</Section>

      {/* Members */}
      <Section title={`Members (${members.length})`}>
        <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-muted)' }}>
          Functional (approver) roles are separate from the access roles above.
          A <strong>FUNC-ADMIN</strong> user can approve any pending approval and
          see everyone&apos;s pending queue (super-approver).
        </p>
        {memberList}
      </Section>

      {canManage && !resourceBilling && <SelfRegistrationCard projectId={projectId} canManage={canManage} />}

      {/* Customer organizations (other tenants using this application) */}
      {canManage && (
        <Section title={`Customer organizations (${projectTenants.length})`}>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-muted)' }}>
            Organizations you have granted the running application to. Each gets
            its <strong>own isolated runtime database</strong>; the first user of
            each organization becomes its <strong>func-admin</strong> and can
            invite their own colleagues.
          </p>
          {tenantList}
        </Section>
      )}

      {/* Pending invitations */}
      {canManage && (
        <Section title={`Pending invitations (${invitations.length})`}>{invitationList}</Section>
      )}

      {/* Role catalog reference */}
      <Section title="Role reference">{roleReference}</Section>

      {dialogs}

      {!isOwner && (
        <p style={{ marginTop: 16, fontSize: 14, color: 'var(--text-muted)' }}>
          You are a member of this project. Only the project owner can delete the project.
        </p>
      )}
    </div>
  )
}

// ---- People tab layout (simple Workbench) --------------------------------

/**
 * The People tab's arrangement of the panel's parts (plan §7): invite inline,
 * people with access, other organizations, how people sign in, then billing
 * and the role reference. Every part is the panel's own JSX and dialogs.
 */
function PeopleLayout(props: {
  projectId: string
  roles: ProjectRoleCatalogEntry[]
  canManage: boolean
  isOwner: boolean
  resourceBilling: boolean
  walletKey: number
  onResourceBilling: (v: boolean) => void
  openInvite: boolean
  onInvited: () => void | Promise<void>
  onAddExisting: () => void
  onInviteMany: () => void
  onShareOrg: () => void
  ownerRow: React.ReactNode
  memberList: React.ReactNode
  memberCount: number
  invitationList: React.ReactNode
  invitationCount: number
  tenantList: React.ReactNode
  tenantCount: number
  roleReference: React.ReactNode
  signIn: React.ReactNode
  dialogs: React.ReactNode
}) {
  const {
    projectId, roles, canManage, isOwner, resourceBilling, walletKey, onResourceBilling, openInvite,
  } = props
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('')
  const [sending, setSending] = useState(false)
  const [canAfford, setCanAfford] = useState(true)
  const [insufficient, setInsufficient] = useState<{ required: string; balance: string } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (openInvite) inputRef.current?.focus()
  }, [openInvite])
  useEffect(() => {
    if (!role && roles.length) {
      const preferred = roles.find((r) => r.role === 'APPLICATION_USER') || roles[0]
      setRole(preferred.role)
    }
  }, [roles, role])

  const hint = (r: ProjectRoleCatalogEntry) =>
    (vocabulary.roleHints as Record<string, string>)[r.role] || r.description || r.display

  const send = async () => {
    if (!email.trim() || !role) {
      toast.error('Enter an email and say what they do')
      return
    }
    setSending(true)
    setInsufficient(null)
    try {
      const out = await sendProjectInvite(projectId, email.trim(), [role])
      if (out.ok) {
        setEmail('')
        await props.onInvited()
      } else if (out.insufficient) {
        setInsufficient(out.insufficient)
      }
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="swb" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {canManage && (
        <section className="swb-card swb-card-pad" aria-labelledby="ppl-invite" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h2 id="ppl-invite" className="swb-section-title">Invite someone</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <div className="swb-field" style={{ flex: '2 1 240px' }}>
              <label htmlFor="inv-email">Email</label>
              <input
                id="inv-email"
                ref={inputRef}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void send() }}
                placeholder="name@company.com"
                className="form-input"
              />
            </div>
            <div className="swb-field" style={{ flex: '1 1 220px' }}>
              <label htmlFor="inv-role">What they do</label>
              <select id="inv-role" value={role} onChange={(e) => setRole(e.target.value)} className="form-input">
                {roles.map((r) => (
                  <option key={r.role} value={r.role}>{hint(r)}</option>
                ))}
              </select>
            </div>
            <button type="button" className="swb-btn swb-btn-primary" onClick={send} disabled={sending || !canAfford}>
              <Mail size={15} /> {sending ? 'Sending…' : 'Send invite'}
            </button>
          </div>
          <InviteCostNotice projectId={projectId} email={email} onAffordability={setCanAfford} />
          {insufficient && (
            <div className="swb-notice swb-notice-warn" style={{ fontSize: 13 }}>
              This application&apos;s wallet holds {insufficient.balance} and {insufficient.required} is needed
              for this seat. Add funds in Billing for people below, then send the invite again.
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm" onClick={props.onAddExisting}>
              <UserPlus size={14} /> Add someone already in your organization
            </button>
            <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm" onClick={props.onInviteMany}>
              <Plus size={14} /> Invite with several roles
            </button>
          </div>
        </section>
      )}

      <section className="swb-card" aria-labelledby="ppl-access">
        <div className="swb-band"><h2 id="ppl-access">People with access</h2>
          <span className="swb-muted">{props.memberCount + 1} {props.memberCount === 0 ? 'person' : 'people'}</span>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {props.ownerRow}
          {props.memberList}
          {props.memberCount === 0 && (
            <p className="swb-muted" style={{ margin: 0 }}>
              Nobody else from your organization yet. People you invite can try the app before it goes live.
            </p>
          )}
          {canManage && props.invitationCount > 0 && (
            <>
              <div className="swb-label">Waiting to accept ({props.invitationCount})</div>
              {props.invitationList}
            </>
          )}
        </div>
      </section>

      {canManage && (
        <section className="swb-card" aria-labelledby="ppl-orgs">
          <div className="swb-band">
            <span style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column' }}>
              <h2 id="ppl-orgs">Other organizations</h2>
              <span className="swb-muted">
                People outside your organization who can open this app. Each organization works in its own
                data; its first person looks after its own colleagues.
              </span>
            </span>
            <button type="button" className="swb-btn swb-btn-secondary" onClick={props.onShareOrg}>
              <Building2 size={15} /> Share with another organization
            </button>
          </div>
          <div style={{ padding: 16 }}>{props.tenantList}</div>
        </section>
      )}

      {props.signIn}

      {canManage && (
        <details className="swb-card" style={{ padding: '0 18px' }}>
          <summary style={{ cursor: 'pointer', minHeight: 52, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>Billing for people</span>
            <span className="swb-muted">Seats, the app&apos;s wallet and self sign-up</span>
          </summary>
          <div style={{ paddingBottom: 16 }}>
            <ProjectWalletCard projectId={projectId} canManage={canManage} refreshKey={walletKey} />
            <ResourceBillingSection projectId={projectId} canManage={canManage} onEnabledChange={onResourceBilling} />
            {!resourceBilling && <SelfRegistrationCard projectId={projectId} canManage={canManage} />}
          </div>
        </details>
      )}

      <details className="swb-card" style={{ padding: '0 18px' }}>
        <summary style={{ cursor: 'pointer', minHeight: 52, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600 }}>What each role can do</span>
        </summary>
        <div style={{ paddingBottom: 16 }}>{props.roleReference}</div>
      </details>

      {props.dialogs}

      {!isOwner && (
        <p className="swb-muted" style={{ margin: 0 }}>
          You are a member of this project. Only the project owner can delete the project.
        </p>
      )}
    </div>
  )
}

// ---- subcomponents -------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
        {title}
      </h3>
      {children}
    </section>
  )
}

function MemberRow({
  member,
  roles,
  canManage,
  isSelf,
  functionalRoles,
  onEditFunctional,
  onEdit,
  onRemove,
}: {
  member: ProjectMemberInfo
  roles: ProjectRoleCatalogEntry[]
  canManage: boolean
  isSelf: boolean
  functionalRoles?: string[]
  onEditFunctional: () => void
  onEdit: () => void
  onRemove: () => void
}) {
  const display = roles.length
    ? member.roles.map((r) => roles.find((x) => x.role === r)?.display || r)
    : member.role_display
  return (
    <div style={{ ...memberRowStyle, ...(member.is_cross_tenant ? crossTenantRowStyle : null) }}>
      <div style={{ minWidth: 0 }}>
        <strong>{member.user_name}</strong>
        {isSelf && <span style={{ marginLeft: 8, fontSize: 13, color: 'var(--accent)' }}>(you)</span>}
        {member.is_cross_tenant && <CrossTenantBadge tenantName={member.tenant_name} />}
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{member.email}</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {display.map((r) => (
            <RoleChip key={r} label={r} />
          ))}
        </div>
        <FunctionalRoleChips roles={functionalRoles} />
      </div>
      {canManage && (
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn-ghost btn-sm" onClick={onEditFunctional} title="Assign functional (approver) roles" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ShieldCheck size={14} /> Functional roles
          </button>
          <button type="button" className="btn-ghost btn-sm" onClick={onEdit} title="Edit roles">
            <Edit3 size={14} />
          </button>
          <button type="button" className="btn-ghost btn-sm" onClick={onRemove} title="Remove member">
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  )
}

// Functional (approver) role chips — uppercased, with FUNC-ADMIN highlighted as
// the super-approver. Renders nothing when the user has no functional roles.
function FunctionalRoleChips({ roles }: { roles?: string[] }) {
  if (!roles || roles.length === 0) {
    return (
      <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-muted)' }}>
        Approver roles: <em>none</em>
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Approver:</span>
      {roles.map((r) => {
        const isAdmin = r.toLowerCase() === FUNC_ADMIN_ROLE
        return (
          <span
            key={r}
            title={isAdmin ? 'Super-approver: can approve any pending approval' : undefined}
            style={{
              fontSize: 13,
              padding: '2px 8px',
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: isAdmin ? 'rgba(245,158,11,0.15)' : 'var(--surface-muted, rgba(0,0,0,0.05))',
              border: `1px solid ${isAdmin ? 'rgba(245,158,11,0.5)' : 'var(--border)'}`,
              color: isAdmin ? '#b45309' : undefined,
              fontWeight: isAdmin ? 600 : undefined,
            }}
          >
            {isAdmin && <ShieldCheck size={11} />}
            {r.toUpperCase()}
          </span>
        )
      })}
    </div>
  )
}

// Badge marking a member who belongs to a DIFFERENT (customer) organization.
function CrossTenantBadge({ tenantName }: { tenantName?: string }) {
  return (
    <span
      title={tenantName ? `External organization: ${tenantName}` : 'External organization'}
      style={{
        marginLeft: 8, fontSize: 13, padding: '1px 8px', borderRadius: 999,
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: 'rgba(79,70,229,0.12)', border: '1px solid rgba(79,70,229,0.4)',
        color: '#4338ca', fontWeight: 600, verticalAlign: 'middle',
      }}
    >
      <Building2 size={11} /> {tenantName || 'External org'}
    </span>
  )
}

// One customer organization (another tenant) granted the running application.
function CustomerTenantCard({ tenant }: { tenant: ProjectTenantInfo }) {
  const statusColor =
    tenant.status === 'active' ? '#059669'
    : tenant.status === 'pending' ? '#d97706'
    : 'var(--text-muted)'
  return (
    <div style={{ padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <Building2 size={18} style={{ color: '#4f46e5', flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <strong>{tenant.tenant_name || 'Organization'}</strong>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {[tenant.email_domain, tenant.web_site].filter(Boolean).join(' · ') || '—'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: statusColor, textTransform: 'capitalize' }}>
            {tenant.status}
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {tenant.user_count} user{tenant.user_count === 1 ? '' : 's'}
          </span>
        </div>
      </div>
      {tenant.status === 'pending' && (
        <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-muted)' }}>
          Waiting for the invited user to accept — their isolated runtime database is
          created on first acceptance.
        </div>
      )}
      {tenant.users.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {tenant.users.map((u) => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 14 }}>
              <span style={{ minWidth: 0 }}>
                <strong>{u.user_name}</strong>{' '}
                <span style={{ color: 'var(--text-muted)' }}>{u.email}</span>
                {/* The role this person ACTUALLY holds in their own
                    organization's database — the rows the runtime reads. This
                    used to badge `first_user_id`, which only records who
                    accepted the grant first, so it kept claiming FUNC-ADMIN
                    for someone the application no longer treated as one. */}
                {(u.functional_roles || []).some((r) => r.toLowerCase() === FUNC_ADMIN_ROLE) && (
                  <span style={{ marginLeft: 6, fontSize: 13, color: '#b45309', fontWeight: 600 }}>FUNC-ADMIN</span>
                )}
                {u.user_id === tenant.first_user_id && (
                  <span style={{ marginLeft: 6, fontSize: 13, color: 'var(--text-muted)' }}>first user</span>
                )}
              </span>
              <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {u.role_display.map((r) => <RoleChip key={r} label={r} />)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function GrantToOrganizationDialog({
  projectId,
  onClose,
  onGranted,
}: {
  projectId: string
  onClose: () => void
  onGranted: () => void
}) {
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    if (!email.trim()) {
      toast.error('Enter the email of an existing user in the other organization')
      return
    }
    setSubmitting(true)
    try {
      const res = await grantToTenant(projectId, email.trim())
      toast.success(
        res.email_sent
          ? `Granted to ${res.tenant.tenant_name || 'the organization'} — invitation sent`
          : `Granted to ${res.tenant.tenant_name || 'the organization'} (email delivery failed)`,
      )
      onGranted()
    } catch (err) {
      const msg = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to grant access'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Grant application to another organization"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={submitting}>Grant access</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>
          Enter the email of an <strong>existing Twasta user</strong> in the other
          organization. They become that organization&apos;s first user and
          func-admin. On acceptance, a dedicated isolated runtime database is
          created for their organization, and they can invite their own colleagues.
        </p>
        <label>
          <div style={{ marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Email</div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="form-input"
            placeholder="admin@customer-org.com"
            style={{ width: '100%' }}
            autoFocus
          />
        </label>
      </div>
    </Modal>
  )
}

function RoleChip({ label }: { label: string }) {
  return (
    <span
      style={{
        fontSize: 13,
        padding: '2px 8px',
        borderRadius: 999,
        background: 'var(--surface-muted, rgba(0,0,0,0.05))',
        border: '1px solid var(--border)',
      }}
    >
      {label}
    </span>
  )
}

function RoleCheckboxList({
  roles,
  selected,
  onChange,
}: {
  roles: ProjectRoleCatalogEntry[]
  selected: Set<string>
  onChange: (next: Set<string>) => void
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {roles.map((r) => {
        const checked = selected.has(r.role)
        return (
          <label key={r.role} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={() => {
                const next = new Set(selected)
                if (checked) next.delete(r.role)
                else next.add(r.role)
                onChange(next)
              }}
              style={{ marginTop: 3 }}
            />
            <span>
              <strong>{r.display}</strong>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{r.description}</div>
            </span>
          </label>
        )
      })}
    </div>
  )
}

function AddExistingUserDialog({
  projectId,
  roleCatalog,
  onClose,
  onAdded,
}: {
  projectId: string
  roleCatalog: ProjectRoleCatalogEntry[]
  onClose: () => void
  onAdded: () => void
}) {
  const [candidates, setCandidates] = useState<CandidateUser[]>([])
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    listCandidateUsers(projectId)
      .then((res) => setCandidates(res.users || []))
      .catch(() => toast.error('Failed to load candidate users'))
      .finally(() => setLoading(false))
  }, [projectId])

  const submit = async () => {
    if (!selectedUserId || selectedRoles.size === 0) {
      toast.error('Pick a user and at least one role')
      return
    }
    setSubmitting(true)
    try {
      await addMember(projectId, selectedUserId, Array.from(selectedRoles))
      toast.success('Member added')
      onAdded()
    } catch (err) {
      const msg = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to add member'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Add existing tenant user"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={submitting}>
            Add to project
          </Button>
        </>
      }
    >
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <div className="spinner" />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label>
            <div style={{ marginBottom: 4, fontSize: 14, fontWeight: 500 }}>User</div>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="form-input"
              style={{ width: '100%' }}
            >
              <option value="">— select a user —</option>
              {candidates.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.user_name} ({u.email})
                </option>
              ))}
            </select>
            {candidates.length === 0 && (
              <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                All tenant users are already on this project (or use invite-by-email for new users).
              </div>
            )}
          </label>
          <div>
            <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Roles</div>
            <RoleCheckboxList roles={roleCatalog} selected={selectedRoles} onChange={setSelectedRoles} />
          </div>
        </div>
      )}
    </Modal>
  )
}

/**
 * Invite one person to the project — the ONE submission path for the Team
 * panel's "Invite by email" dialog and the People tab's inline invite. A
 * project-wallet 402 comes back as `insufficient` (the remedy — top up and
 * retry — only makes sense where the invite was made); everything else is
 * toasted here.
 */
export async function sendProjectInvite(
  projectId: string, email: string, roles: string[],
): Promise<{ ok: boolean; insufficient?: { required: string; balance: string } }> {
  try {
    const res = await inviteToProject(projectId, email, roles)
    if (res.mode === 'added_existing') {
      toast.success('User added (already in your tenant)')
    } else if (res.email_sent) {
      toast.success('Invitation email sent')
    } else {
      toast.success('Invitation created (email delivery failed; share the link manually)')
    }
    return { ok: true }
  } catch (err) {
    const response = (err as {
      response?: { status?: number; data?: {
        detail?: string; code?: string
        required_display?: string; balance_display?: string
      } }
    }).response
    if (response?.status === 402 && response.data?.code === 'INSUFFICIENT_PROJECT_CREDITS') {
      return { ok: false, insufficient: {
        required: response.data.required_display ?? '—',
        balance: response.data.balance_display ?? '—',
      } }
    }
    toast.error(response?.data?.detail || 'Failed to invite')
    return { ok: false }
  }
}

function InviteByEmailDialog({
  projectId,
  roleCatalog,
  onClose,
  onInvited,
}: {
  projectId: string
  roleCatalog: ProjectRoleCatalogEntry[]
  onClose: () => void
  onInvited: () => void
}) {
  const [email, setEmail] = useState('')
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [canAfford, setCanAfford] = useState(true)
  // The project-wallet 402. The global interceptor deliberately ignores this
  // code — the wallet is the PROJECT's, and the remedy (top it up, retry the
  // invite) only makes sense right here.
  const [insufficient, setInsufficient] = useState<{ required: string; balance: string } | null>(null)

  const submit = async () => {
    if (!email.trim() || selectedRoles.size === 0) {
      toast.error('Enter an email and pick at least one role')
      return
    }
    setSubmitting(true)
    setInsufficient(null)
    try {
      const out = await sendProjectInvite(projectId, email.trim(), Array.from(selectedRoles))
      if (out.ok) onInvited()
      else if (out.insufficient) setInsufficient(out.insufficient)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Invite by email"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={submitting} disabled={!canAfford}>
            Send invitation
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <label>
          <div style={{ marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Email</div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="form-input"
            placeholder="teammate@example.com"
            style={{ width: '100%' }}
          />
          <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-muted)' }}>
            If the email belongs to a user already in your tenant, they're added immediately. Otherwise an invitation
            email is sent.
          </div>
        </label>
        <div>
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Roles</div>
          <RoleCheckboxList roles={roleCatalog} selected={selectedRoles} onChange={setSelectedRoles} />
        </div>

        <InviteCostNotice projectId={projectId} email={email} onAffordability={setCanAfford} />

        {insufficient && (
          <div style={{
            padding: '10px 12px', borderRadius: 6, fontSize: 13,
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-danger, #d64545)',
          }}>
            This application&apos;s wallet holds {insufficient.balance} and{' '}
            {insufficient.required} is needed for this seat. Add funds from the
            wallet card above, then retry.
            <div style={{ marginTop: 8 }}>
              <Button variant="secondary" onClick={submit} loading={submitting}>
                Retry invite
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function EditRolesDialog({
  projectId,
  member,
  roleCatalog,
  onClose,
  onSaved,
}: {
  projectId: string
  member: ProjectMemberInfo
  roleCatalog: ProjectRoleCatalogEntry[]
  onClose: () => void
  onSaved: () => void
}) {
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set(member.roles))
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    if (selectedRoles.size === 0) {
      toast.error('Pick at least one role (or remove the member instead)')
      return
    }
    setSubmitting(true)
    try {
      await updateMemberRoles(projectId, member.id, Array.from(selectedRoles))
      toast.success('Roles updated')
      onSaved()
    } catch (err) {
      const msg = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to update roles'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit roles — ${member.user_name}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={submitting}>
            Save roles
          </Button>
        </>
      }
    >
      <RoleCheckboxList roles={roleCatalog} selected={selectedRoles} onChange={setSelectedRoles} />
    </Modal>
  )
}

// Editor for a user's functional (approver) roles. Offers the available roles
// as a checkbox list (always including FUNC-ADMIN and any role the user already
// holds) plus an input to add a brand-new role. Stores roles in canonical
// lower-case, displays them upper-case. Saving an empty set clears the user's
// functional roles.
function EditFunctionalRolesDialog({
  projectId,
  userId,
  userName,
  current,
  roleCatalog,
  onClose,
  onSaved,
}: {
  projectId: string
  userId: string
  userName: string
  current: string[]
  roleCatalog: string[]
  onClose: () => void
  onSaved: () => void
}) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(current.map((r) => r.toLowerCase())),
  )
  // The full option set: catalog ∪ already-held ∪ the reserved FUNC-ADMIN, plus
  // any roles the user types in (kept in `extra` so they show even before save).
  const [extra, setExtra] = useState<string[]>([])
  const [newRole, setNewRole] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const options = Array.from(new Set([
    FUNC_ADMIN_ROLE,
    ...roleCatalog.map((r) => r.toLowerCase()),
    ...current.map((r) => r.toLowerCase()),
    ...extra,
  ]))

  const toggle = (role: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(role)) next.delete(role)
      else next.add(role)
      return next
    })
  }

  const addNewRole = () => {
    const role = newRole.trim().toLowerCase()
    if (!role) return
    if (!options.includes(role)) setExtra((prev) => [...prev, role])
    setSelected((prev) => new Set(prev).add(role))
    setNewRole('')
  }

  const submit = async () => {
    setSubmitting(true)
    try {
      await setUserFunctionalRoles(projectId, userId, Array.from(selected))
      toast.success('Functional roles updated')
      onSaved()
    } catch (err) {
      const msg = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        || 'Failed to update functional roles'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Functional roles — ${userName}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={submitting}>
            Save functional roles
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>
          Functional (approver) roles are separate from access roles.
          A <strong>FUNC-ADMIN</strong> user can approve any pending approval and
          see everyone&apos;s pending queue (super-approver).
        </p>

        {options.length === 0 ? (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>
            No roles defined yet — add one below.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {options.map((role) => {
              const isAdmin = role === FUNC_ADMIN_ROLE
              return (
                <label key={role} style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selected.has(role)}
                    onChange={() => toggle(role)}
                  />
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {isAdmin && <ShieldCheck size={13} style={{ color: '#b45309' }} />}
                    <strong>{role.toUpperCase()}</strong>
                    {isAdmin && (
                      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>super-approver</span>
                    )}
                  </span>
                </label>
              )
            })}
          </div>
        )}

        <div>
          <div style={{ marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Add a new role</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNewRole() } }}
              className="form-input"
              placeholder="e.g. QC"
              style={{ flex: 1 }}
            />
            <Button variant="secondary" size="sm" icon={<Plus size={14} />} onClick={addNewRole} disabled={!newRole.trim()}>
              Add
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ---- styles --------------------------------------------------------------

const ownerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 14px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface-muted, rgba(0,0,0,0.02))',
}

const memberRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 14px',
  border: '1px solid var(--border)',
  borderRadius: 6,
}

// Subtle highlight for members from a different (customer) organization.
const crossTenantRowStyle: React.CSSProperties = {
  borderColor: 'rgba(79,70,229,0.4)',
  background: 'rgba(79,70,229,0.04)',
}
